import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';

function useStaggeredCards(count: number) {
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const [visible, setVisible] = useState<boolean[]>(Array(count).fill(false));
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    refs.current.forEach((el, i) => {
      if (!el) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisible((prev) => { const next = [...prev]; next[i] = true; return next; });
              observer.disconnect();
            }
          });
        },
        { threshold: 0.2 }
      );
      observer.observe(el);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [count]);
  return { refs, visible };
}

function TrustCard({ item, index, cardRef, visible }: {
  item: { icon: string; title: string; description: string };
  index: number;
  cardRef: (el: HTMLDivElement | null) => void;
  visible: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      ref={cardRef}
      className="flex items-start gap-4 rounded-2xl p-5 sm:p-6 cursor-default"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? (hovered ? 'translateY(-4px)' : 'translateY(0px)') : 'translateY(36px)',
        transition: `opacity 0.55s cubic-bezier(0.22,1,0.36,1) ${index * 0.1}s, transform ${hovered ? '0.2s' : '0.4s'} cubic-bezier(0.22,1,0.36,1)`,
        background: hovered ? 'rgba(196,168,130,0.10)' : 'rgba(196,168,130,0.04)',
        border: `1px solid ${hovered ? 'rgba(196,168,130,0.28)' : 'rgba(196,168,130,0.12)'}`,
        boxShadow: hovered ? '0 0 20px rgba(196,168,130,0.08), inset 0 1px 0 rgba(196,168,130,0.1)' : 'none',
      }}
    >
      <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-xl transition-all duration-200"
        style={{
          background: hovered ? 'rgba(196,168,130,0.18)' : 'rgba(196,168,130,0.1)',
          border: `1px solid ${hovered ? 'rgba(196,168,130,0.35)' : 'rgba(196,168,130,0.2)'}`,
        }}
      >
        <i className={`${item.icon} text-lg transition-all duration-200`} style={{ color: hovered ? '#e8d8c0' : '#c4a882' }}></i>
      </div>
      <div>
        <h3 className="text-[13px] sm:text-[15px] font-bold mb-1 transition-colors duration-200" style={{ color: hovered ? '#ffffff' : 'rgba(255,255,255,0.9)' }}>{item.title}</h3>
        <p className="text-xs sm:text-sm leading-relaxed transition-colors duration-200" style={{ color: hovered ? 'rgba(196,168,130,0.7)' : 'rgba(196,168,130,0.5)' }}>{item.description}</p>
      </div>
    </div>
  );
}

export default function TrustSection() {
  const { lang } = useLanguage();

  const t = {
    en: {
      h2Part1: 'Independent Expertise You Can Trust',
      cards: [
        { icon: 'ri-shield-check-line', title: '100% Independent', description: 'No dealership or OEM affiliations. Our goal is to find the right vehicle for you. We represent your interests.' },
        { icon: 'ri-cpu-line', title: 'AI + Human Expertise', description: 'Advanced technology combined with years of automotive industry experience.' },
        { icon: 'ri-time-line', title: 'Save Time & Money', description: 'Skip weeks of research and avoid costly mistakes with expert guidance.' },
        { icon: 'ri-file-list-3-line', title: 'Comprehensive Analysis', description: 'Every make, model, and trim analyzed to find your perfect match.' },
        { icon: 'ri-customer-service-2-line', title: 'Ongoing Support', description: "Questions after your Discovery? We're here to help throughout your journey." },
        { icon: 'ri-star-line', title: 'Satisfaction Guaranteed', description: 'Hundreds of satisfied clients who found their ideal vehicle with our help.' }
      ],
      footerCopyright: '© 2025 Automatchlab. All rights reserved.',
    },
    fr: {
      h2Part1: 'Expertise indépendante en qui vous pouvez avoir confiance',
      cards: [
        { icon: 'ri-shield-check-line', title: '100% Indépendant', description: "Nous n'avons aucune affiliation avec des concessionnaires. Notre seul objectif est de trouver le bon véhicule pour vous." },
        { icon: 'ri-cpu-line', title: 'IA + Expertise Humaine', description: "Technologie avancée combinée à des années d'expérience dans l'industrie automobile." },
        { icon: 'ri-time-line', title: 'Économisez Temps et Argent', description: 'Évitez des semaines de recherche et évitez les erreurs coûteuses grâce à des conseils d\'experts.' },
        { icon: 'ri-file-list-3-line', title: 'Analyse Complète', description: 'Chaque marque, modèle et finition analysés pour trouver votre correspondance parfaite.' },
        { icon: 'ri-customer-service-2-line', title: 'Support Continu', description: 'Des questions après votre consultation? Nous sommes là pour vous aider tout au long de votre parcours.' },
        { icon: 'ri-star-line', title: 'Satisfaction Garantie', description: "Des centaines de clients satisfaits qui ont trouvé leur véhicule idéal avec notre aide." }
      ],
      footerCopyright: '© 2025 Automatchlab. Tous droits réservés.',
    },
  };

  const text = t[lang];
  const cards = text.cards;
  const { refs, visible } = useStaggeredCards(cards.length);

  return (
    <>
      {/* Section 1: Trust cards */}
      <section id="trust" className="relative pt-12 sm:pt-20 lg:pt-24 pb-14 sm:pb-20 overflow-hidden" style={{ background: '#121220' }}>
        <style>{`
          @keyframes shimmer-trust { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
          @keyframes card-rise { from { opacity: 0; transform: translateY(36px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>

        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(196,168,130,0.04) 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 30%, rgba(196,168,130,0.03) 0%, transparent 60%)' }} />
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(196,168,130,0.2), transparent)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-20">
          <div className="text-left sm:text-center mb-10 sm:mb-14">
            <h2 className="text-[15px] sm:text-[40px] lg:text-6xl font-black text-white mb-2 sm:mb-4 tracking-tight" style={{ fontFamily: 'Satoshi, Inter, sans-serif', letterSpacing: '-0.02em' }}>
              {text.h2Part1}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {cards.map((item, index) => (
              <TrustCard
                key={index}
                item={item}
                index={index}
                cardRef={(el) => { refs.current[index] = el; }}
                visible={visible[index]}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="pt-5 sm:pt-7 pb-4 sm:pb-6" style={{ background: '#0c0c16', borderTop: '1px solid rgba(196,168,130,0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-20">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
              {(['ri-linkedin-fill', 'ri-twitter-x-fill', 'ri-instagram-fill', 'ri-youtube-fill'] as const).map((icon, i) => (
                <a key={i} href="#" className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:-translate-y-1 cursor-pointer" style={{ background: 'rgba(196,168,130,0.06)', border: '1px solid rgba(196,168,130,0.15)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(196,168,130,0.15)'; (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(196,168,130,0.35)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(196,168,130,0.06)'; (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(196,168,130,0.15)'; }}
                >
                  <i className={`${icon} text-base`} style={{ color: 'rgba(196,168,130,0.6)' }}></i>
                </a>
              ))}
            </div>
            <p className="text-[11px] sm:text-sm" style={{ color: 'rgba(196,168,130,0.35)' }}>{text.footerCopyright}</p>
          </div>
        </div>
      </footer>
    </>
  );
}
